import { useEffect, useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Anchor, UserRound, Scissors } from "lucide-react";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function startGoogleAuth(role) {
  const redirectUrl = window.location.origin + `/?signup_role=${role}`;
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

const BG = "https://static.prod-images.emergentagent.com/jobs/18558f8e-d20a-418c-ad14-9bf948245a62/images/16e4f44db9ebf94598c6f50c9d2d5bcbc1ce76ce104025c8ad10693752a47ee8.png";

const ALLOWED = ["patient", "surgeon"];

export default function RegisterPage() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const initialRole = ALLOWED.includes(searchParams.get("role")) ? searchParams.get("role") : "patient";
  const [role, setRole] = useState(initialRole);
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  useEffect(() => { setRole(initialRole); }, [initialRole]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const r = await register({ ...form, email: form.email.trim().toLowerCase(), role });
    setBusy(false);
    if (r.ok) { toast.success("Account created"); nav("/"); }
    else toast.error(r.error);
  };

  const isSurgeon = role === "surgeon";

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-slate-50">
      <div className="relative hidden lg:block" style={{ backgroundImage: `url(${BG})`, backgroundSize: "cover", backgroundPosition: "center" }}>
        <div className="absolute inset-0 bg-[#339999]/55" />
        <div className="relative z-10 p-12 text-white h-full flex flex-col justify-between">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-md bg-white/15 backdrop-blur flex items-center justify-center"><Anchor className="h-5 w-5" /></div>
            <span className="font-semibold text-lg tracking-tight" style={{ fontFamily: "Outfit, sans-serif" }}>AAMG</span>
          </div>
          <div className="max-w-md space-y-3">
            <p className="aamg-label text-white/70">{isSurgeon ? "Surgeon registration" : "Patient registration"}</p>
            <h1 className="text-3xl xl:text-4xl font-semibold leading-tight" style={{ fontFamily: "Outfit, sans-serif" }}>
              {isSurgeon ? "Request anesthesia coverage for your cases." : "Your pre-op intake, simplified."}
            </h1>
            <p className="text-white/80 text-sm leading-relaxed">
              {isSurgeon
                ? "Send procedure requests to a specific anesthetist or to any available. Your data is encrypted at rest."
                : "Complete your registration online before surgery. Your information is encrypted and visible only to your care team."}
            </p>
          </div>
        </div>
      </div>

      <div className="flex flex-col justify-center px-6 sm:px-12 lg:px-20 py-12">
        <div className="w-full max-w-md mx-auto" data-testid="register-card">
          <p className="aamg-label mb-2">Create account</p>
          <h2 className="text-3xl font-semibold text-slate-900 mb-1" style={{ fontFamily: "Outfit" }}>Sign up</h2>
          <p className="text-slate-500 text-sm mb-6">Choose your role.</p>

          <Tabs value={role} onValueChange={setRole} className="mb-5">
            <TabsList className="grid grid-cols-2 w-full bg-slate-100" data-testid="register-role-tabs">
              <TabsTrigger value="patient" data-testid="register-tab-patient" className="data-[state=active]:bg-white data-[state=active]:text-[#339999] data-[state=active]:shadow-sm">
                <UserRound className="h-3.5 w-3.5 mr-1.5" /> Patient
              </TabsTrigger>
              <TabsTrigger value="surgeon" data-testid="register-tab-surgeon" className="data-[state=active]:bg-white data-[state=active]:text-[#339999] data-[state=active]:shadow-sm">
                <Scissors className="h-3.5 w-3.5 mr-1.5" /> Surgeon
              </TabsTrigger>
            </TabsList>
          </Tabs>

          <form onSubmit={submit} className="space-y-4">
            <div><Label className="field-label">Full name</Label><Input required value={form.name} onChange={set("name")} data-testid="register-name-input" /></div>
            <div><Label className="field-label">Email</Label><Input type="email" required value={form.email} onChange={set("email")} data-testid="register-email-input" /></div>
            <div><Label className="field-label">Phone</Label><Input value={form.phone} onChange={set("phone")} data-testid="register-phone-input" /></div>
            <div>
              <Label className="field-label">Password</Label>
              <Input type="password" required minLength={6} value={form.password} onChange={set("password")} data-testid="register-password-input" />
              <p className="text-xs text-slate-400 mt-1">At least 6 characters.</p>
            </div>
            <Button type="submit" disabled={busy} className="w-full h-11 bg-[#339999] hover:bg-[#2A7F7F]" data-testid="register-submit-btn">
              {busy ? "Creating…" : `Create ${role} account`}
            </Button>
          </form>

          <div className="my-5 flex items-center gap-3 text-xs text-slate-400">
            <div className="flex-1 h-px bg-slate-200" /> OR <div className="flex-1 h-px bg-slate-200" />
          </div>
          <Button onClick={() => startGoogleAuth(role)} variant="outline" className="w-full h-11 border-slate-300" data-testid="register-google-btn">
            <svg className="h-4 w-4 mr-2" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.9 32.6 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.2-.1-2.3-.4-3.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 18.9 13 24 13c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.3 0 10.1-2 13.7-5.3l-6.3-5.3C29.3 35 26.8 36 24 36c-5.3 0-9.8-3.4-11.3-8L6 28c3.3 6.6 10 10 18 10z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.7 2-2 3.7-3.6 5l6.3 5.3C42 35 44 30 44 24c0-1.2-.1-2.3-.4-3.5z"/></svg>
            Continue with Google
          </Button>

          <p className="text-center text-sm text-slate-500 mt-6">
            Already registered? <Link to="/login" className="text-[#339999] font-medium hover:underline">Sign in</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
